import apiClient from './api';
import { API_ENDPOINTS } from '@/config/api.config';
import {
  CourseDeliveryType,
  CourseStructureAnalysis,
  CourseStructureAnalysisResponse,
  DocumentStore,
  DocumentStoreResponse,
  DocumentStoresListResponse,
  CreateDocumentStoreRequest,
  UpdateDocumentStoreRequest,
  ListDocumentStoresRequest,
  DeleteDocumentStoreResponse,
  SearchDocumentContentRequest,
  SearchDocumentContentResponse,
  DropdownListResponse,
  DocumentUrlInput,
  DocumentUrl,
} from '@/types/document-store.types';

/**
 * Document Store Service
 * Manages knowledge bases and document repositories
 * Following DEVGUIDE.md standards - NO hardcoded URLs
 */
class DocumentStoreService {
  // ==========================================
  // CRUD OPERATIONS
  // ==========================================

  /**
   * Create a new document store
   * @param request - Document store creation data
   * @returns Created document store
   */
  async createDocumentStore(request: CreateDocumentStoreRequest): Promise<DocumentStore> {
    const response = await apiClient.post<DocumentStoreResponse>(
      API_ENDPOINTS.DOCUMENT_STORE.CREATE,
      request
    );

    if (!response.data.success) {
      throw new Error(response.data.description || 'Failed to create document store');
    }

    return response.data.data;
  }

  /**
   * Get document store by ID
   * @param id - MongoDB ObjectId of the document store
   * @returns Document store details
   */
  async getDocumentStoreById(id: string): Promise<DocumentStore> {
    const response = await apiClient.get<DocumentStoreResponse>(
      API_ENDPOINTS.DOCUMENT_STORE.GET_BY_ID(id)
    );

    if (!response.data.success) {
      throw new Error(response.data.description || 'Failed to fetch document store');
    }

    return response.data.data;
  }

  /**
   * Update an existing document store
   * @param id - MongoDB ObjectId of the document store
   * @param request - Update data (partial update supported)
   * @returns Updated document store
   */
  async updateDocumentStore(id: string, request: UpdateDocumentStoreRequest): Promise<DocumentStore> {
    const response = await apiClient.put<DocumentStoreResponse>(
      API_ENDPOINTS.DOCUMENT_STORE.UPDATE(id),
      request
    );

    if (!response.data.success) {
      throw new Error(response.data.description || 'Failed to update document store');
    }

    return response.data.data;
  }

  /**
   * Delete a document store (soft delete)
   * @param id - MongoDB ObjectId of the document store
   * @returns Deletion confirmation
   */
  async deleteDocumentStore(id: string): Promise<{ message: string }> {
    const response = await apiClient.delete<DeleteDocumentStoreResponse>(
      API_ENDPOINTS.DOCUMENT_STORE.DELETE(id)
    );

    if (!response.data.success) {
      throw new Error(response.data.description || 'Failed to delete document store');
    }

    return response.data.data;
  }

  /**
   * List document stores with pagination and filtering
   * @param filters - Query parameters for filtering and pagination
   * @returns Paginated list of document stores
   */
  async listDocumentStores(filters?: ListDocumentStoresRequest): Promise<DocumentStoresListResponse> {
    // Changed to POST for better filter support and pagination
    const response = await apiClient.post<DocumentStoresListResponse>(
      API_ENDPOINTS.DOCUMENT_STORE.LIST,
      filters || {}
    );

    if (!response.data.success) {
      throw new Error(response.data.description || 'Failed to fetch document stores');
    }

    return response.data;
  }

  /**
   * Get overall metrics for all document stores
   * Lightweight endpoint that returns aggregated stats without full document details
   * @returns Aggregated metrics
   */
  async getMetrics(): Promise<{
    totalStores: number;
    storesWithProcessing: number;
    totalDocuments: number;
    processingDocuments: number;
    completedDocuments: number;
    failedDocuments: number;
  }> {
    const response = await apiClient.get<APIResponse<{
      totalStores: number;
      storesWithProcessing: number;
      totalDocuments: number;
      processingDocuments: number;
      completedDocuments: number;
      failedDocuments: number;
    }>>(API_ENDPOINTS.DOCUMENT_STORE.METRICS);

    if (!response.data.success) {
      throw new Error(response.data.description || 'Failed to fetch metrics');
    }

    return response.data.data;
  }

  // ==========================================
  // BUSINESS OPERATIONS
  // ==========================================

  /**
   * Search document content using semantic search with LLM enrichment
   * @param request - Search query and knowledge base name with optional parameters
   * @returns Search results with enhanced content and raw data
   */
  async searchDocumentContent(request: SearchDocumentContentRequest): Promise<{
    content: string;
    raw_data: string[];
    scores?: number[];
    chunks_count: number;
  }> {
    const response = await apiClient.post<SearchDocumentContentResponse>(
      API_ENDPOINTS.DOCUMENT_STORE.SEARCH,
      {
        ...request,
        top_k: request.top_k || 10,
        include_scores: request.include_scores || true,
      }
    );

    if (!response.data.success) {
      throw new Error(response.data.description || 'Failed to search document content');
    }

    return {
      content: response.data.data.content,
      raw_data: response.data.data.raw_data || [],
      scores: response.data.data.scores,
      chunks_count: response.data.data.chunks_count || 0,
    };
  }

  /**
   * Get dropdown list of document stores
   * @returns Simplified list for dropdown selection
   */
  async getDropdownList(): Promise<Array<{ _id: string; name: string }>> {
    const response = await apiClient.get<DropdownListResponse>(
      API_ENDPOINTS.DOCUMENT_STORE.DROPDOWN_LIST
    );

    if (!response.data.success) {
      throw new Error(response.data.description || 'Failed to fetch dropdown list');
    }

    return response.data.data.saved_documents;
  }

  /**
   * Get the range of course structure a knowledge store's documents support.
   *
   * The server reads the documents at most once per set of them and caches the result on the
   * store, so this is cheap to call: selecting a store, or switching course type, is served
   * from that cache. Only a store whose documents have changed — or one that predates the
   * feature and has never been analysed — pays for a fresh read, and it is cached afterwards.
   *
   * Advisory only — an analysis that cannot run comes back with analysis_available: false
   * rather than throwing, so it never blocks course creation.
   *
   * @param id - MongoDB ObjectId of the document store
   * @param options - The selected course type (which decides whether scenes, a duration, or
   *   neither is suggested) and the builder's own ceilings, so the suggestion is always
   *   something the admin can enter.
   */
  async getCourseStructureAnalysis(
    id: string,
    options: {
      delivery_type: CourseDeliveryType;
      max_modules?: number;
      max_content_items_per_module?: number;
      min_scenes_per_content?: number;
      max_scenes_per_content?: number;
      min_duration_seconds?: number;
      max_duration_seconds?: number;
      force_refresh?: boolean;
    }
  ): Promise<CourseStructureAnalysis> {
    const response = await apiClient.get<CourseStructureAnalysisResponse>(
      API_ENDPOINTS.DOCUMENT_STORE.COURSE_STRUCTURE_ANALYSIS(id),
      { params: options }
    );

    if (!response.data.success) {
      throw new Error(response.data.description || 'Failed to analyse the knowledge store');
    }

    return response.data.data;
  }

  // ==========================================
  // DOCUMENT MANAGEMENT OPERATIONS
  // ==========================================

  /**
   * Add a document to a document store
   * @param storeId - MongoDB ObjectId of the document store
   * @param document - Document details to add
   * @returns Updated document store
   */
  async addDocumentToStore(storeId: string, document: DocumentUrlInput): Promise<DocumentStore> {
    // Get current store
    const store = await this.getDocumentStoreById(storeId);
    
    // Add new document to the list
    const updatedDocuments = [...store.document_urls, document];
    
    // Update the store
    return await this.updateDocumentStore(storeId, {
      document_urls: updatedDocuments,
    });
  }

  /**
   * Remove a document from a document store
   * @param storeId - MongoDB ObjectId of the document store
   * @param documentId - Document ID to remove
   * @returns Updated document store
   */
  async removeDocumentFromStore(storeId: string, documentId: string): Promise<DocumentStore> {
    // Get current store
    const store = await this.getDocumentStoreById(storeId);

    // Filter out the document, keep _id so backend can match existing docs
    const updatedDocuments = store.document_urls
      .filter(doc => doc._id !== documentId)
      .map(doc => ({
        _id: doc._id,
        name: doc.name,
        key: doc.key,
        url: doc.url,
        fileType: doc.fileType,
        status: doc.status,
        useAsExample: doc.useAsExample,
      }));

    // Update the store
    return await this.updateDocumentStore(storeId, {
      document_urls: updatedDocuments,
    });
  }

  /**
   * Update a document's example status
   * @param storeId - MongoDB ObjectId of the document store
   * @param documentId - Document ID to update
   * @param useAsExample - Whether to use as example
   * @returns Updated document store
   */
  async updateDocumentExampleStatus(
    storeId: string,
    documentId: string,
    useAsExample: boolean
  ): Promise<DocumentStore> {
    // Get current store
    const store = await this.getDocumentStoreById(storeId);

    // Update the specific document, keep _id so backend can match existing docs
    const updatedDocuments = store.document_urls.map(doc => ({
      _id: doc._id,
      name: doc.name,
      key: doc.key,
      url: doc.url,
      fileType: doc.fileType,
      useAsExample: doc._id === documentId ? useAsExample : doc.useAsExample,
    }));

    // Update the store
    return await this.updateDocumentStore(storeId, {
      document_urls: updatedDocuments,
    });
  }

  /**
   * Batch add documents to a store
   * @param storeId - MongoDB ObjectId of the document store
   * @param documents - Array of documents to add
   * @returns Updated document store
   */
  async batchAddDocuments(storeId: string, documents: DocumentUrlInput[]): Promise<DocumentStore> {
    // Get current store
    const store = await this.getDocumentStoreById(storeId);
    
    // Add new documents to the list
    const updatedDocuments = [...store.document_urls, ...documents];
    
    // Update the store
    return await this.updateDocumentStore(storeId, {
      document_urls: updatedDocuments,
    });
  }

  // ==========================================
  // UTILITY METHODS
  // ==========================================

  /**
   * Check if a document store name exists
   * @param name - Name to check
   * @returns True if name exists
   */
  async checkNameExists(name: string): Promise<boolean> {
    try {
      const response = await this.listDocumentStores({ name, limit: 1 });
      return response.data.length > 0 && response.data[0].name.toLowerCase() === name.toLowerCase();
    } catch (error) {
      console.error('Error checking name existence:', error);
      return false;
    }
  }

  /**
   * Get document stores with processed documents
   * @returns Document stores that have at least one processed document
   */
  async getStoresWithProcessedDocuments(): Promise<DocumentStore[]> {
    const response = await this.listDocumentStores({ limit: 100 });
    return response.data.filter(store => 
      store.document_urls.some(doc => doc.status === 'DONE')
    );
  }

  /**
   * Get processing statistics for a store
   * @param storeId - MongoDB ObjectId of the document store
   * @returns Processing statistics
   */
  async getProcessingStats(storeId: string): Promise<{
    total: number;
    processed: number;
    processing: number;
    failed: number;
    exampleCount: number;
  }> {
    const store = await this.getDocumentStoreById(storeId);

    return {
      total: store.document_urls.length,
      processed: store.document_urls.filter(d => d.status === 'DONE').length,
      processing: store.document_urls.filter(d => d.status === 'IN_PROGRESS').length,
      failed: store.document_urls.filter(d => d.status === 'FAILED').length,
      exampleCount: store.document_urls.filter(d => d.useAsExample).length,
    };
  }

  /**
   * Enhance document store description using AI
   * @param name - Document store name
   * @param description - Current description
   * @returns Enhanced description with suggestions and key topics
   */
  async enhanceDescription(
    name: string,
    description: string
  ): Promise<{
    enhanced_description: string;
    suggestions: string[];
    key_topics: string[];
  }> {
    const response = await apiClient.post<APIResponse<{
      enhanced_description: string;
      suggestions: string[];
      key_topics: string[];
    }>>(
      API_ENDPOINTS.DOCUMENT_STORE.ENHANCE_DESCRIPTION,
      { name, description }
    );

    if (!response.data.success) {
      throw new Error(response.data.description || 'Failed to enhance description');
    }

    return response.data.data;
  }
}

// Export singleton instance
export const documentStoreService = new DocumentStoreService();